import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { LifeBuoy, Plus, MessageSquare, X, Paperclip, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_FILES = 5;
const MAX_BYTES = 5 * 1024 * 1024;

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    const base64 = result.split(',')[1] || result;
    resolve(base64);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const STATUS_BADGE = {
  open: { label: 'Open', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  resolved: { label: 'Resolved', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  closed: { label: 'Closed', cls: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200' }
};

const PRIORITY_DOT = { low: 'bg-emerald-500', medium: 'bg-amber-500', high: 'bg-rose-500' };

export default function ContactSupport() {
  const { api } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [replyFiles, setReplyFiles] = useState([]); // attachments for the reply box
  const [files, setFiles] = useState([]); // [{filename, mime, size, content_base64}]

  const [form, setForm] = useState({
    category: 'general', subject: '', description: '', priority: 'medium'
  });

  const fetchTickets = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const r = await api.get('/support/tickets');
      setTickets(r.data || []);
    } catch {
      toast.error('Failed to load tickets');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleFiles = async (e, target = 'new') => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ''; // reset so the same file can be re-picked after removal
    if (!picked.length) return;
    const current = target === 'reply' ? replyFiles : files;
    if (current.length + picked.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} attachments allowed`);
      return;
    }
    const next = [...current];
    for (const f of picked) {
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} exceeds 5MB limit`);
        continue;
      }
      try {
        const content_base64 = await fileToBase64(f);
        next.push({ filename: f.name, mime: f.type || 'application/octet-stream', size: f.size, content_base64 });
      } catch {
        toast.error(`Failed to read ${f.name}`);
      }
    }
    if (target === 'reply') setReplyFiles(next); else setFiles(next);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));
  const removeReplyFile = (idx) => setReplyFiles(replyFiles.filter((_, i) => i !== idx));

  const createTicket = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error('Subject and description are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        attachments: files.map(({ filename, mime, content_base64 }) => ({ filename, mime, content_base64 }))
      };
      await api.post('/support/tickets', payload);
      toast.success('Support ticket created — your team has been notified by email');
      setForm({ category: 'general', subject: '', description: '', priority: 'medium' });
      setFiles([]);
      setShowNew(false);
      fetchTickets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create ticket');
    }
    setSubmitting(false);
  };

  const sendReply = async () => {
    if ((!replyText.trim() && replyFiles.length === 0) || !selected) return;
    setReplySubmitting(true);
    try {
      const payload = {
        message: replyText,
        attachments: replyFiles.map(({ filename, mime, content_base64 }) => ({ filename, mime, content_base64 }))
      };
      const r = await api.post(`/support/tickets/${selected.id}/reply`, payload);
      setSelected(r.data);
      setTickets(prev => prev.map(t => t.id === r.data.id ? r.data : t));
      setReplyText('');
      setReplyFiles([]);
    } catch {
      toast.error('Failed to send reply');
    }
    setReplySubmitting(false);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="contact-support-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white">
              <LifeBuoy className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Contact Support</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">Raise a ticket — our team will be notified by email and respond shortly.</p>
            </div>
          </div>
          <Button onClick={() => setShowNew(true)} className="gap-2" data-testid="new-ticket-btn">
            <Plus className="w-4 h-4" /> New Ticket
          </Button>
        </div>

        {/* Two-pane layout: list + detail */}
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
          {/* Tickets list */}
          <Card className="border border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">My Tickets ({tickets.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 text-center text-slate-400 text-sm">Loading...</div>
              ) : tickets.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">No tickets yet</div>
              ) : (
                <div className="divide-y divide-slate-200 dark:divide-slate-700 max-h-[600px] overflow-y-auto">
                  {tickets.map(t => (
                    <button
                      key={t.id}
                      onClick={() => { setSelected(t); setReplyText(''); setReplyFiles([]); }}
                      className={`w-full text-left p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition ${selected?.id === t.id ? 'bg-indigo-50 dark:bg-indigo-900/20 border-l-2 border-l-indigo-500' : ''}`}
                      data-testid={`ticket-row-${t.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[t.priority] || PRIORITY_DOT.medium}`} />
                        <span className="text-xs font-mono text-slate-400">{t.id}</span>
                        <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ml-auto ${STATUS_BADGE[t.status]?.cls || ''}`}>
                          {STATUS_BADGE[t.status]?.label || t.status}
                        </Badge>
                      </div>
                      <p className="font-medium text-sm text-slate-900 dark:text-slate-100 truncate">{t.subject}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{new Date(t.created_at).toLocaleDateString()} · {t.replies?.length || 0} {t.replies?.length === 1 ? 'reply' : 'replies'}</p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ticket detail */}
          <Card className="border border-slate-200 dark:border-slate-700">
            <CardContent className="p-6">
              {!selected ? (
                <div className="text-center py-16">
                  <MessageSquare className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                  <p className="text-slate-400 text-sm">Select a ticket to view details</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="ticket-detail">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-mono text-slate-400">{selected.id}</p>
                      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mt-1">{selected.subject}</h2>
                    </div>
                    <Badge className={STATUS_BADGE[selected.status]?.cls}>{STATUS_BADGE[selected.status]?.label}</Badge>
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">{selected.description}</p>

                  {selected.attachments?.length > 0 && (
                    <div className="text-xs text-slate-500" data-testid="ticket-attachments">
                      <span className="font-medium">Attachments:</span>{' '}
                      {selected.attachments.map((a, i) => (
                        <span key={i} className="inline-flex items-center gap-1 mr-2">
                          <Paperclip className="w-3 h-3" />{a.filename} ({(a.size/1024).toFixed(1)} KB)
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Replies */}
                  {selected.replies?.length > 0 && (
                    <div className="space-y-3 border-t border-slate-200 dark:border-slate-700 pt-4">
                      {selected.replies.map((r, i) => (
                        <div key={i} className={`flex ${r.sender_type === 'admin' ? 'justify-start' : 'justify-end'}`}>
                          <div className={`max-w-[80%] rounded-lg p-3 ${r.sender_type === 'admin' ? 'bg-slate-100 dark:bg-slate-800' : 'bg-indigo-100 dark:bg-indigo-900/40'}`}>
                            <p className="text-xs font-medium mb-1 text-slate-600 dark:text-slate-300">{r.sender_name} · {new Date(r.created_at).toLocaleString()}</p>
                            <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                            {r.attachments?.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300 space-y-0.5" data-testid={`reply-attachments-${i}`}>
                                {r.attachments.map((a, ai) => (
                                  <div key={ai} className="inline-flex items-center gap-1 mr-2">
                                    <Paperclip className="w-3 h-3" />
                                    <span>{a.filename}</span>
                                    <span className="text-slate-400">({(a.size/1024).toFixed(1)} KB)</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply input (only for open tickets) */}
                  {selected.status !== 'closed' && (
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-2">
                      <Textarea
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        placeholder="Type your reply..."
                        rows={3}
                        data-testid="reply-input"
                      />

                      {/* Reply attachments */}
                      <div>
                        <input
                          type="file"
                          multiple
                          onChange={e => handleFiles(e, 'reply')}
                          className="hidden"
                          id="reply-attachment-input"
                          data-testid="reply-attachment-input"
                        />
                        <label
                          htmlFor="reply-attachment-input"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-dashed border-slate-300 dark:border-slate-600 rounded-md text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                        >
                          <Paperclip className="w-3.5 h-3.5" /> Attach files <span className="text-slate-400">(max {MAX_FILES} · 5 MB each)</span>
                        </label>
                        {replyFiles.length > 0 && (
                          <ul className="mt-2 space-y-1" data-testid="reply-attachment-list">
                            {replyFiles.map((f, i) => (
                              <li key={i} className="flex items-center justify-between text-sm bg-slate-50 dark:bg-slate-800 rounded px-3 py-1.5">
                                <span className="truncate flex-1 mr-2">
                                  <Paperclip className="w-3 h-3 inline mr-1.5 text-slate-400" />
                                  {f.filename}
                                  <span className="text-xs text-slate-400 ml-2">{(f.size/1024).toFixed(1)} KB</span>
                                </span>
                                <button
                                  onClick={() => removeReplyFile(i)}
                                  className="text-slate-400 hover:text-rose-500"
                                  data-testid={`remove-reply-attachment-${i}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <Button
                        onClick={sendReply}
                        disabled={replySubmitting || (!replyText.trim() && replyFiles.length === 0)}
                        data-testid="send-reply-btn"
                      >
                        {replySubmitting ? 'Sending...' : 'Send Reply'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* New-ticket modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <CardTitle>Create Support Ticket</CardTitle>
                <button onClick={() => setShowNew(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="ticket-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="bug">Bug Report</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                      <SelectItem value="feature_request">Feature Request</SelectItem>
                      <SelectItem value="access_issue">Access Issue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="ticket-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  value={form.subject}
                  onChange={e => setForm({ ...form, subject: e.target.value })}
                  placeholder="Brief summary of the issue"
                  data-testid="ticket-subject"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Steps to reproduce, screenshots, what you expected, what happened..."
                  rows={5}
                  data-testid="ticket-description"
                />
              </div>

              {/* Attachments */}
              <div>
                <Label>Attachments <span className="text-xs font-normal text-slate-400">(optional, max {MAX_FILES} files · 5 MB each)</span></Label>
                <div className="mt-2">
                  <input
                    type="file"
                    multiple
                    onChange={handleFiles}
                    className="hidden"
                    id="ticket-attachment-input"
                    data-testid="ticket-attachment-input"
                  />
                  <label
                    htmlFor="ticket-attachment-input"
                    className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 dark:border-slate-600 rounded-md text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                  >
                    <Paperclip className="w-4 h-4" /> Add files
                  </label>
                  {files.length > 0 && (
                    <ul className="mt-2 space-y-1" data-testid="attachment-list">
                      {files.map((f, i) => (
                        <li key={i} className="flex items-center justify-between text-sm bg-slate-50 dark:bg-slate-800 rounded px-3 py-1.5">
                          <span className="truncate flex-1 mr-2">
                            <Paperclip className="w-3 h-3 inline mr-1.5 text-slate-400" />
                            {f.filename}
                            <span className="text-xs text-slate-400 ml-2">{(f.size/1024).toFixed(1)} KB</span>
                          </span>
                          <button
                            onClick={() => removeFile(i)}
                            className="text-slate-400 hover:text-rose-500"
                            data-testid={`remove-attachment-${i}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                <Button onClick={createTicket} disabled={submitting} data-testid="submit-ticket-btn">
                  {submitting ? 'Creating...' : 'Create Ticket'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
}
